import { HTMLAttributes, forwardRef } from 'react';
import './Grid.css';

export type GridColumns = 1 | 2 | 3 | 4 | 6 | 12 | 'auto';
export type GridSpacing = 'none' | 'sm' | 'md' | 'lg' | 'xl';

export interface GridProps extends HTMLAttributes<HTMLDivElement> {
  /** Number of columns */
  columns?: GridColumns;
  /** Gap between grid items */
  spacing?: GridSpacing;
  /** Minimum width for auto-fit columns (used when columns="auto") */
  minColumnWidth?: string;
}

/**
 * Grid component for responsive grid layouts.
 *
 * @example
 * ```tsx
 * <Grid columns={3} spacing="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 *   <Card>Item 3</Card>
 * </Grid>
 * ```
 */
export const Grid = forwardRef<HTMLDivElement, GridProps>(
  (
    {
      columns = 'auto',
      spacing = 'md',
      minColumnWidth = '250px',
      className = '',
      style,
      children,
      ...props
    },
    ref
  ) => {
    const classNames = [
      'ui-grid',
      columns !== 'auto' && `ui-grid--cols-${columns}`,
      columns === 'auto' && 'ui-grid--auto',
      `ui-grid--spacing-${spacing}`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const customStyle =
      columns === 'auto'
        ? { ...style, '--ui-grid-min-column-width': minColumnWidth }
        : style;

    return (
      <div
        ref={ref}
        className={classNames}
        style={customStyle as React.CSSProperties}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Grid.displayName = 'Grid';
